/* eslint-disable no-useless-catch */
/* eslint-disable no-extra-boolean-cast */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAccountActions } from '../../redux/actions/accountActions';
import { authManager } from '../../auth/authManager';

export type ApiState<T> = {
    getAsync: (getUrl?: string) => Promise<T | T[] | any>;
    postAsync: <U>(obj?: U, postUrl?: string) => Promise<any | void>;
    postFormDataAsync: <U>(formData: FormData, postUrl?: string) => Promise<U>;
    putAsync: <U>(obj: U, putUrl?: string) => Promise<any | void>;
    deleteAsync: (ids: string[], deleteUrl?: string) => Promise<any | void>;
}
export enum HTTPContentType {
    json = 'application/json',
    text = 'text/plain',
    html = 'text/html',
    binary = 'application/octet-stream'
}
export enum HTTPStatusCode {
    created = 201,
    updated = 200,
}

class ApiError extends Error {
    status: number;

    constructor(status: number, message: string) {
        super(`HTTP ${status}: ${message}`);
        this.name = 'ApiError';
        this.status = status;
    }
}

export const getHTTPHeaders = (contentType?: string) => {
    const headers: Record<string, string> = { Accept: contentType || HTTPContentType.json };
    if (contentType) headers['Content-Type'] = contentType;
    return headers;
}

export const useApi = <T>(baseUrl: string): ApiState<T> => {

    //Hooks
    const { clearAccounts, clearPrimaryAccount } = useAccountActions();
    
    //General Logic
    const logout = () => {
        authManager.clearTokens();
        clearAccounts();
        clearPrimaryAccount();
    }

    const isApiError = (error: unknown): error is ApiError => {
        return error instanceof ApiError;
    }

    const tryRecoverAuth = async () => {
        const recovered = await authManager.tryReauthenticate();
        if (!recovered) {
            logout();
        }

        return recovered;
    }

    const createUrl = (actionUrl?: string) => {
        if (!actionUrl) return `/${baseUrl}`;
        else if (actionUrl.startsWith('/')) return actionUrl;
        else return `/${baseUrl}/${actionUrl}`;
    }
    const handleResponse = async<T>(res: Response): Promise<T> => {
        const contentType = res.headers.get('content-type') || '';
        const isJson = contentType.includes('application/json') || contentType.includes('+json');
        const body = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

        if (!res.ok) {
            const message =
                (body && (body.message || body.error || body.title)) || res.statusText || 'Request failed';
            throw new ApiError(res.status, message);
        }

        return body as T;
    }

    const requestWithRecovery = async<T>(requestFactory: () => Promise<Response>, allowRetry = true): Promise<T> => {
        try {
            const res = await requestFactory();
            return await handleResponse<T>(res);
        } catch (error) {
            if (allowRetry && isApiError(error) && error.status === 401) {
                const recovered = await tryRecoverAuth();
                if (recovered) {
                    try {
                        const retryRes = await requestFactory();
                        return await handleResponse<T>(retryRes);
                    } catch (retryError) {
                        if (isApiError(retryError) && retryError.status === 401) {
                            logout();
                        }

                        throw retryError;
                    }
                }
            }

            throw error;
        }
    }

    //CRUD Methods
    const getAsync = async (getUrl?: string): Promise<T | T[] | any> => {
        try {
            return await requestWithRecovery<T | T[] | any>(() => {
                return fetch(createUrl(getUrl), {
                    method: 'GET',
                    headers: getHTTPHeaders(HTTPContentType.json),
                });
            });
        } catch (error) {
            if (isApiError(error) && error.status === 404) {
                return undefined;
            }

            throw error;
        }
    }
    
    const postAsync = async <U>(obj?: U, postUrl?: string): Promise<any | void> => {
        try {
            return await requestWithRecovery<U>(() => {
                return fetch(createUrl(postUrl), {
                    method: 'POST',
                    headers: getHTTPHeaders(HTTPContentType.json),
                    body: JSON.stringify(obj),
                });
            });
        } catch (error) {
            throw error;
        }
    };

    const postFormDataAsync = async <U>(formData: FormData, postUrl?: string): Promise<U> => {
        return await requestWithRecovery<U>(() => {
            return fetch(createUrl(postUrl), {
                method: 'POST',
                headers: getHTTPHeaders(),
                body: formData,
            });
        });
    };

    const putAsync = async <U>(obj: U, updateUrl?: string): Promise<any | void> => {
        try {
            return await requestWithRecovery<U>(() => {
                return fetch(createUrl(updateUrl), {
                    method: 'PUT',
                    headers: getHTTPHeaders(HTTPContentType.json),
                    body: JSON.stringify(obj),
                });
            });
        } catch (error) {
            throw error;
        }
    }

    const deleteAsync = async (ids: string[], deleteUrl?: string): Promise<any | void> => {
        try {
            return await requestWithRecovery<any>(() => {
                return fetch(createUrl(deleteUrl), {
                    method: 'DELETE',
                    headers: getHTTPHeaders(HTTPContentType.json),
                    body: JSON.stringify(ids)
                });
            });
        } catch (error) {
            throw error;
        }
    }

    return {
        getAsync,
        postAsync,
        postFormDataAsync,
        putAsync,
        deleteAsync,
    };
}